import { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { EmptyState, Spinner } from '../../ui';
import type {
  KnowledgeGateway,
  // (type import keeps the viewer decoupled from concrete gateways)
} from '../../features/knowledge/knowledgeGateway';
import type { SpecDocument } from '../../features/knowledge/types';
import { MarkdownView } from './MarkdownView';

interface SpecDocViewerProps {
  gateway: KnowledgeGateway;
  packageName: string;
  doc: SpecDocument | null;
  revisionId?: string;
}

/**
 * Loads and renders a single spec document's markdown via the gateway. Owns its
 * own fetch lifecycle (loading/error) keyed on the selected document.
 */
export function SpecDocViewer({
  gateway,
  packageName,
  doc,
  revisionId,
}: SpecDocViewerProps) {
  const [markdown, setMarkdown] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!doc) {
      setMarkdown('');
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    gateway
      .getDocument(packageName, doc.path, revisionId)
      .then((content) => {
        if (cancelled) return;
        if (!content) {
          setError('Document content is unavailable.');
          setMarkdown('');
        } else {
          setMarkdown(content.markdown);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load document.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gateway, packageName, doc, revisionId]);

  if (!doc) {
    return (
      <EmptyState
        icon={<FileText className="h-6 w-6" />}
        title="Select a document"
        description="Pick a spec document from the list to read its contents."
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner label="Loading document" />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon={<FileText className="h-6 w-6" />}
        title="Couldn't load document"
        description={error}
      />
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center gap-2 border-b border-surface-border pb-2">
        <FileText className="h-4 w-4 text-accent" />
        <span className="text-sm font-semibold text-zinc-100">{doc.title}</span>
        <span className="font-mono text-xs text-zinc-500">{doc.path}</span>
      </div>
      <MarkdownView markdown={markdown} />
    </div>
  );
}

export default SpecDocViewer;
